import { Component, DestroyRef, inject, Input, Renderer2 } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, combineLatest, EMPTY, map, Observable, of, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AgentStepModel, AttachedDocument, StepType } from 'src/app/models/api/agentStep.model';
import { Evidence } from 'src/app/models/api/finding.model';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import {
  ActionGetAgentStepSensitiveDataResponseModel,
  AiAgentStepsHttpService,
  SensitiveDataRequestActions,
} from 'src/app/services/httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service';
import { AiFindingsHttpService } from 'src/app/services/httpServices/detection/aiFindingsHttpService/ai-findings-http.service';
import { SidePanelService } from '../../../features/side-panel/services/side-panel.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { Label } from 'src/app/labels/englishLabels';
import { CommonModule } from '@angular/common';
import { AnchorLinkComponent } from 'src/app/components/anchor-button/anchor-link.component';
import { GetSensitiveDataActionComponent } from 'src/app/components/get-sensitive-data-input/get-sensitive-data-input.component';
import { BadgeComponent } from 'src/app/components/badge/badge.component';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-evidence-list',
  standalone: true,
  templateUrl: './evidence-list.component.html',
  styleUrls: ['./evidence-list.component.scss'],
  imports: [
    CommonModule,
    AnchorLinkComponent,
    GetSensitiveDataActionComponent,
    BeautifyDisplayStringPipe,
    MatTooltipModule,
    LabelsProviderPipe,
    BadgeComponent,
  ],
})
export class EvidenceListComponent {
  @Input() public evidenceList: Evidence[] = [];
  @Input() public relatedSteps: AgentStepModel[];
  @Input() public findingId?: string;
  @Input() public integrationId?: string;
  @Input() public sourcePipeline?: string;

  private readonly _locationSeparator = ' > ';
  private readonly _sensitiveSourceKey = 'sensitiveSource';
  private readonly _sensitiveContentStartIndexesKey = 'sensitiveContentStartIndexes';
  private readonly _fileContentLocation = 'File content';
  private readonly _httpClient = inject(HttpClient);
  private readonly _renderer = inject(Renderer2);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _notificationService = inject(NotificationsService);

  constructor(
    private readonly _beautifyDisplayStringPipe: BeautifyDisplayStringPipe,
    private readonly _aiAgentStepsService: AiAgentStepsHttpService,
    private readonly _aiFindingsService: AiFindingsHttpService,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _sidePanelService: SidePanelService
  ) {}

  public getStepType(evidence: Evidence): StepType | undefined {
    const stepDetails = this.getStepDetailsFromEvidence(evidence);
    if (!stepDetails) {
      return undefined;
    }
    return stepDetails.type;
  }

  public getLocation(evidence: Evidence): string {
    let result = '';
    if (!evidence.location) {
      return result;
    }
    const splitLocation = evidence.location.split('.');
    if (splitLocation.length > 0) {
      const formattedString = this._beautifyDisplayStringPipe.transform(splitLocation[splitLocation.length - 1]);
      result += `${this._locationSeparator}${formattedString}`;
    }
    return result;
  }

  public isSensitiveContent(evidence: Evidence): boolean {
    const data = this._getAdditionalDataRecord(evidence);
    return (
      data?.[this._sensitiveContentStartIndexesKey] !== undefined || data?.[this._sensitiveSourceKey] !== undefined
    );
  }

  public getEvidenceSensitiveIndexes(evidence: Evidence): string | undefined {
    return this._getAdditionalDataRecord(evidence)?.[this._sensitiveContentStartIndexesKey];
  }

  private static readonly _IAM_DEMO_FINDING_IDS = new Set([
    'finding-prompt-injection-exfil',
    'finding-data-exfiltration-send-email',
    'finding-intent-breaking-send-email',
    'finding-taint-analysis-exfil',
    'spaiware-finding-indirect-prompt-injection',
    'spaiware-finding-memory-update',
    'spaiware-finding-http-exfil',
    'spaiware-finding-malicious-memory',
    'spaiware-finding-suspicious-url',
    'spaiware-finding-special-characters',
    'spaiware-finding-blank-image-call',
    'cursor-mcp-finding-prod-pii-query',
    'cursor-mcp-finding-unapproved-mcp-access',
    'cursor-mcp-finding-exfil-write',
    'cursor-mcp-blocked-prod-pii-query',
    'cursor-mcp-blocked-unapproved-mcp-access',
    'cursor-mcp-blocked-exfil-write',
  ]);

  public getSensitiveDataMethod(evidence: Evidence): () => Observable<ActionGetAgentStepSensitiveDataResponseModel> {
    if (evidence.stepId?.startsWith('rsa-') || EvidenceListComponent._IAM_DEMO_FINDING_IDS.has(this.findingId ?? '')) {
      return () => this._fetchMockSensitiveData(evidence.stepId);
    }

    const stepDetails = this.getStepDetailsFromEvidence(evidence);
    const stepType = stepDetails?.type;

    // Use new evidence-based API only for user/AI message steps when evidence has an ID
    const isMessageStep = stepType === StepType.userMessageStep || stepType === StepType.aiMessageStep;
    if (evidence.id && this.integrationId && this.findingId && isMessageStep) {
      return () =>
        this._aiFindingsService
          .getEvidenceSensitiveContent(
            this.integrationId!,
            this.findingId!,
            evidence.id!,
            this.sourcePipeline === 'inline' ? 'inline' : undefined
          )
          .pipe(
            map((response) => ({
              resultCode: 'success',
              title: response.fileName || 'Evidence Content',
              transcript: response.sensitiveContent,
              fileDownloadUrl: response.fileDownloadUrl,
            }))
          );
    }

    // Use step-based API for all other cases
    if (!stepDetails) {
      return () =>
        this._aiAgentStepsService.getAgentStepSensitiveData(
          this.relatedSteps[0].integration.id,
          this.relatedSteps[0].id,
          SensitiveDataRequestActions.transcript,
          undefined,
          this.findingId
        );
    }
    const sensitiveRequest: string | undefined = this._getAdditionalDataRecord(evidence)?.[this._sensitiveSourceKey];
    if (sensitiveRequest && this.isSensitiveValueValid(sensitiveRequest)) {
      const type = SensitiveDataRequestActions[sensitiveRequest];
      return () =>
        this._aiAgentStepsService.getAgentStepSensitiveData(
          stepDetails.integration.id,
          stepDetails.id,
          type,
          undefined,
          this.findingId
        );
    }
    switch (stepDetails.type) {
      case StepType.toolRequestStep:
        return () =>
          this._aiAgentStepsService.getAgentStepSensitiveData(
            stepDetails.integration.id,
            stepDetails.id,
            SensitiveDataRequestActions.invocationParams,
            undefined,
            this.findingId
          );
      case StepType.toolResponseStep:
        return () =>
          this._aiAgentStepsService.getAgentStepSensitiveData(
            stepDetails.integration.id,
            stepDetails.id,
            SensitiveDataRequestActions.invocationResult,
            undefined,
            this.findingId
          );
      case StepType.userMessageStep:
      case StepType.aiMessageStep:
        return () =>
          this._aiAgentStepsService.getAgentStepSensitiveData(
            stepDetails.integration.id,
            stepDetails.id,
            SensitiveDataRequestActions.transcript,
            undefined,
            this.findingId
          );
      case StepType.toolInvocationStep:
        return () =>
          this._aiAgentStepsService.getAgentStepSensitiveData(
            stepDetails.integration.id,
            stepDetails.id,
            SensitiveDataRequestActions.invocationResult,
            undefined,
            this.findingId
          );
      case StepType.ragStep:
        return () =>
          this._aiAgentStepsService.getAgentStepSensitiveData(
            stepDetails.integration.id,
            stepDetails.id,
            SensitiveDataRequestActions.snippet,
            undefined,
            this.findingId
          );
      case StepType.triggerStep:
        return () =>
          this._aiAgentStepsService.getAgentStepSensitiveData(
            stepDetails.integration.id,
            stepDetails.id,
            SensitiveDataRequestActions.payload,
            undefined,
            this.findingId
          );
      default:
        return () =>
          this._aiAgentStepsService.getAgentStepSensitiveData(
            this.relatedSteps[0].integration.id,
            this.relatedSteps[0].id,
            SensitiveDataRequestActions.transcript,
            undefined,
            this.findingId
          );
    }
  }

  public getCoreEvidence(evidence: Evidence): string {
    // Find anything between < and >
    let coreEvidence = evidence.coreEvidence;
    const pattern = /<([^>]+)>/g;
    if (this.isSensitiveContent(evidence)) {
      if (!evidence.coreEvidence.toLowerCase().includes('fetch content')) {
        coreEvidence += ' ' + this._labelsProviderPipe.transform('evidenceListPage.sensitiveContentEvidence');
      }
    }
    return coreEvidence.replace(pattern, '<strong>$1</strong>');
  }

  public openEvidence(evidence: Evidence) {
    const stepDetails = this.getStepDetailsFromEvidence(evidence);
    if (!stepDetails) {
      return;
    }
    this._sidePanelService.openAgentStepDetailsFromFinding(stepDetails.thread.id, stepDetails.id, stepDetails);
  }

  public isFileContentEvidence(evidence: Evidence): boolean {
    return evidence.location === this._fileContentLocation;
  }

  public getAttachedDocuments(evidence: Evidence): AttachedDocument[] {
    const stepDetails = this.getStepDetailsFromEvidence(evidence);
    if (!stepDetails || !('attachedDocuments' in stepDetails)) {
      return [];
    }
    return (stepDetails as { attachedDocuments?: AttachedDocument[] }).attachedDocuments || [];
  }

  public downloadAttachment(evidence: Evidence, attachment: AttachedDocument): void {
    const stepDetails = this.getStepDetailsFromEvidence(evidence);
    if (!stepDetails || !this.integrationId) {
      return;
    }

    this._aiAgentStepsService
      .downloadAiAgentStepAttachment(attachment.id, attachment.displayName, this.integrationId, stepDetails.id)
      .pipe(
        tap((response) => {
          const anchor: HTMLAnchorElement = this._renderer.createElement('a');
          anchor.href = response.fileUrl;
          anchor.download = attachment.displayName;
          anchor.target = '_blank';
          anchor.click();
          this._notificationService.success(Label.get('evidenceListPage.attachmentDownloadedSuccessfully'));
        }),
        catchError(() => {
          this._notificationService.error(Label.get('evidenceListPage.attachmentDownloadFailed'));
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private getStepDetailsFromEvidence(evidence: Evidence): AgentStepModel | undefined {
    return this.relatedSteps.find((step) => step.id === evidence.stepId);
  }

  private isSensitiveValueValid(val: string): val is keyof typeof SensitiveDataRequestActions {
    return Object.keys(SensitiveDataRequestActions).includes(val);
  }

  private _getAdditionalDataRecord(evidence: Evidence): Record<string, string> | undefined {
    return typeof evidence.additionalData === 'string' ? undefined : evidence.additionalData;
  }

  private _fetchMockSensitiveData(stepId: string): Observable<ActionGetAgentStepSensitiveDataResponseModel> {
    type ContentMap = Record<string, ActionGetAgentStepSensitiveDataResponseModel>;
    const notFound = {
      resultCode: 'error',
      title: 'Not Found',
      transcript: '',
    } as ActionGetAgentStepSensitiveDataResponseModel;

    const rsa$ = this._httpClient
      .get<ContentMap>('assets/mockData/rsaFetchContentMockData.json')
      .pipe(catchError(() => of({} as ContentMap)));

    const iam$ = this._httpClient
      .get<ContentMap>('assets/mockData/iamDemoFetchContentMockData.json')
      .pipe(catchError(() => of({} as ContentMap)));

    return combineLatest([rsa$, iam$]).pipe(
      map(([rsaData, iamData]) => iamData[stepId] ?? rsaData[stepId] ?? notFound)
    );
  }
}
